import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ABTestTableHeaderProps {
  showProductColumn: boolean;
  readOnly: boolean;
}

function HeaderCell({ label, tooltip, className }: { label: string; tooltip: React.ReactNode; className: string }) {
  return (
    <div className={cn(className, "py-2 px-3 font-semibold text-xs flex items-center")}>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="border-b border-dotted border-muted-foreground cursor-default">{label}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[260px]">{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function ABTestTableHeader({ showProductColumn, readOnly }: ABTestTableHeaderProps) {
  return (
    <div className="flex border-b border-border text-foreground">
      {showProductColumn && (
        <div className={cn(
          "relative w-[288px] shrink-0 py-2 px-3 font-semibold text-xs flex items-center z-30",
          "sticky left-0 bg-card",
          "after:content-[''] after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0",
          "after:w-2 after:translate-x-full after:bg-gradient-to-r after:from-foreground/10 after:to-transparent",
          "after:opacity-0 after:transition-opacity [[data-scrolled=true]_&]:after:opacity-100",
        )}>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="border-b border-dotted border-muted-foreground cursor-default">Product</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px]">The hero ASIN this test is running on.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      <HeaderCell label="Test Name" tooltip="The target intent, keyword, or both being tested." className="flex-1 min-w-[160px]" />
      <HeaderCell label="Source" tooltip="Which surface in the Create A/B Test flow produced Variant B." className="w-[160px] shrink-0" />
      <HeaderCell label="Scope" tooltip="Which listing fields (Title, Bullets, etc.) are being modified in this test." className="w-[120px] shrink-0" />
      <div className="w-[100px] shrink-0 py-2 px-3 font-semibold text-xs flex items-center">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="border-b border-dotted border-muted-foreground cursor-default">Status</span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[220px]">
              <p><span className="font-medium">Planning</span> &mdash; Draft, not yet live.</p>
              <p><span className="font-medium">Running</span> &mdash; Active experiment in progress.</p>
              <p><span className="font-medium">Complete</span> &mdash; Experiment finished with results.</p>
              <p><span className="font-medium">Archived</span> &mdash; Excluded from active dashboards.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <HeaderCell label="Result" tooltip="The winning variant and conversion lift percentage from the Amazon experiment." className="w-[180px] shrink-0" />
      <HeaderCell label="Conv. (A / B)" tooltip="Conversion rate for Variant A vs Variant B from the Amazon experiment report." className="w-[130px] shrink-0" />
      <HeaderCell label="Sales (A / B)" tooltip="Total sales for Variant A vs Variant B during the experiment period." className="w-[140px] shrink-0" />
      <HeaderCell label="Date Started" tooltip="When the experiment started." className="w-[110px] shrink-0" />
      <HeaderCell label="Date Compl." tooltip="When the experiment was completed. Dash if still running." className="w-[110px] shrink-0" />
      <HeaderCell label="Listing Change" tooltip="Whether the winning variant has been submitted as a listing change." className="w-[200px] shrink-0" />
      <div className="w-[120px] shrink-0 py-2 px-3 font-semibold text-xs flex items-center justify-center">
        {!readOnly ? "Actions" : "View"}
      </div>
    </div>
  );
}
